/**
 * „Zuletzt geöffnet" (LS-Muster): schnelle Rücksprung-Karten auf den
 * Dashboards. Bewusst eine reine Browser-Convenience in `localStorage` —
 * pro Gerät, ohne Backend; jede Lese-/Schreibstelle ist abgesichert, weil
 * Speicherzugriff je nach Browser-Einstellung fehlen darf.
 */

export type ZuletztTyp = "projekt" | "onboarding";

export type ZuletztEintrag = {
	typ: ZuletztTyp;
	id: string;
	titel: string;
	untertitel: string;
	/** ISO-Zeitpunkt des letzten Besuchs — neueste zuerst. */
	besuchtAm: string;
};

const KEY = "olgen.zuletzt-geoeffnet";
const KONTO_KEY = "olgen.aktives-konto";
const MAX = 4;

/**
 * Die Merkliste gehört zum KONTO, nicht zum Browser: Der AppProvider trägt
 * die Id des angemeldeten Kontos hier ein, und Lesen wie Schreiben hängen
 * daran. Sonst sah ein Kundenkonto im selben Browser die Team-Karten (Fund
 * der Kundensicht-Begehung am 02.09.2026).
 */
export function aktivesKontoSetzen(userId: string | null): void {
	try {
		if (userId === null) localStorage.removeItem(KONTO_KEY);
		else localStorage.setItem(KONTO_KEY, userId);
	} catch {
		// ohne Speicher keine Merkliste
	}
}

function schluessel(): string | null {
	try {
		const konto = localStorage.getItem(KONTO_KEY);
		return konto === null || konto === "" ? null : `${KEY}:${konto}`;
	} catch {
		return null;
	}
}

export function zuletztGeoeffnet(): ZuletztEintrag[] {
	try {
		const key = schluessel();
		if (key === null) return [];
		const roh = localStorage.getItem(key);
		if (roh === null) return [];
		const daten: unknown = JSON.parse(roh);
		if (!Array.isArray(daten)) return [];
		return daten.filter(
			(e): e is ZuletztEintrag =>
				typeof e === "object" &&
				e !== null &&
				"typ" in e &&
				(e.typ === "projekt" || e.typ === "onboarding") &&
				"id" in e &&
				typeof e.id === "string" &&
				"titel" in e &&
				typeof e.titel === "string" &&
				"untertitel" in e &&
				typeof e.untertitel === "string" &&
				"besuchtAm" in e &&
				typeof e.besuchtAm === "string",
		);
	} catch {
		return [];
	}
}

/** Merkt einen Besuch: dedupliziert je (typ, id), neueste zuerst, max 4. */
export function merkeGeoeffnet(eintrag: Omit<ZuletztEintrag, "besuchtAm">): void {
	try {
		const key = schluessel();
		if (key === null) return;
		const ohne = zuletztGeoeffnet().filter((e) => !(e.typ === eintrag.typ && e.id === eintrag.id));
		const neu: ZuletztEintrag[] = [
			{ ...eintrag, besuchtAm: new Date().toISOString() },
			...ohne,
		].slice(0, MAX);
		localStorage.setItem(key, JSON.stringify(neu));
	} catch {
		// Speicher gesperrt (Privatmodus o. ä.) — dann eben keine Merkliste.
	}
}
